import React, { useState } from 'react';
import { X, FileSpreadsheet, Layers, Image as ImageIcon, Loader2, CheckCircle } from 'lucide-react';
import { MenuData } from '../types';
import { downloadImagesZip } from '../services/imageService';

interface ExportModalProps {
  isOpen: boolean;
  data: MenuData;
  onClose: () => void;
  onExportTemplate1: () => void;
  onExportTemplate2: () => void;
  onExportBoth: () => void;
  onOpenImageEditor: () => void;
}

export const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  data,
  onClose,
  onExportTemplate1,
  onExportTemplate2,
  onExportBoth,
  onOpenImageEditor,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showImageResizeConfirm, setShowImageResizeConfirm] = useState(false);

  const handleDownloadImagesClick = () => {
    setShowImageResizeConfirm(true);
  };

  const handleConfirmResize = (shouldResize: boolean) => {
    setShowImageResizeConfirm(false);
    if (shouldResize) {
        onClose();
        onOpenImageEditor();
    } else {
        executeDownloadImages();
    }
  };

  const executeDownloadImages = async () => {
    setIsDownloading(true);
    setIsComplete(false);
    setProgress(0);
    try {
      await downloadImagesZip(data, (p) => setProgress(p));
      setIsComplete(true);
      setTimeout(() => {
        setIsDownloading(false);
        setIsComplete(false);
        onClose();
      }, 1500);
    } catch (error) {
      console.error("Download failed", error);
      setIsDownloading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200 relative">
        
        {/* Image Resize Confirmation Overlay */}
        {showImageResizeConfirm && (
            <div className="absolute inset-0 bg-white z-50 flex flex-col items-center justify-center p-8 animate-in fade-in text-center">
                <div className="bg-orange-100 p-4 rounded-full mb-4">
                    <ImageIcon size={32} className="text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">Resize Images?</h3>
                <p className="text-slate-600 mb-8">
                    Do you want to resize and format your images for specific aggregators (Talabat, Deliveroo, etc.) before downloading?
                </p>
                <div className="flex flex-col gap-3 w-full">
                    <button 
                        onClick={() => handleConfirmResize(true)}
                        className="w-full py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-colors"
                    >
                        Yes, Open Image Editor
                    </button>
                    <button 
                        onClick={() => handleConfirmResize(false)}
                        className="w-full py-3 bg-slate-100 text-slate-700 rounded-lg font-bold hover:bg-slate-200 transition-colors"
                    >
                        No, Just Download Original
                    </button>
                </div>
            </div>
        )}

        {isDownloading && (
            <div className="absolute inset-0 bg-white z-50 flex flex-col items-center justify-center p-8 animate-in fade-in">
                {isComplete ? (
                    <>
                        <CheckCircle size={48} className="text-emerald-500 mb-4 animate-in zoom-in duration-300" />
                        <h3 className="text-lg font-bold text-slate-800 mb-2">Download Complete!</h3>
                        <p className="text-sm text-slate-500 font-medium">Your images have been zipped successfully.</p>
                    </>
                ) : (
                    <>
                        <Loader2 size={48} className="text-orange-500 animate-spin mb-4" />
                        <h3 className="text-lg font-bold text-slate-800 mb-2">Preparing Images...</h3>
                        <div className="w-full bg-slate-100 rounded-full h-2.5 mb-2 overflow-hidden">
                            <div className="bg-orange-500 h-2.5 rounded-full transition-all duration-300" style={{ width: `${progress}%` }}></div>
                        </div>
                        <p className="text-sm text-slate-500 font-medium">{progress}% Complete</p>
                        <p className="text-xs text-slate-400 mt-2">Please wait while we zip your files.</p>
                    </>
                )}
            </div>
        )}

        <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50/50">
          <h3 className="font-bold text-slate-800 flex items-center gap-2">
            <FileSpreadsheet className="text-emerald-600" size={20} />
            Export Menu
          </h3>
          <button 
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-1 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="p-6 space-y-3">
          <p className="text-sm text-slate-500 mb-4">
            Choose an export format for your menu data.
          </p>

          <button
            onClick={onExportTemplate1}
            className="w-full flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all group text-left"
          >
            <div>
              <div className="font-semibold text-slate-700 group-hover:text-emerald-700">Template 1 (Standard)</div>
              <div className="text-xs text-slate-500 mt-1">Current export format with yellow styling</div>
            </div>
            <FileSpreadsheet className="text-slate-300 group-hover:text-emerald-500" size={24} />
          </button>

          <button
            onClick={onExportTemplate2}
            className="w-full flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-purple-500 hover:bg-purple-50 transition-all group text-left"
          >
            <div>
              <div className="font-semibold text-slate-700 group-hover:text-purple-700">Template 2 (New Layout)</div>
              <div className="text-xs text-slate-500 mt-1">Purple headers, specific columns, no legend</div>
            </div>
            <FileSpreadsheet className="text-slate-300 group-hover:text-purple-500" size={24} />
          </button>

          <button
            onClick={onExportBoth}
            className="w-full flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-all group text-left"
          >
            <div>
              <div className="font-semibold text-slate-700 group-hover:text-blue-700">Both Templates</div>
              <div className="text-xs text-slate-500 mt-1">Single file with two sheets (Template 1 & 2)</div>
            </div>
            <Layers className="text-slate-300 group-hover:text-blue-500" size={24} />
          </button>

          <button
            onClick={handleDownloadImagesClick}
            className="w-full flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-orange-500 hover:bg-orange-50 transition-all group text-left"
          >
            <div>
              <div className="font-semibold text-slate-700 group-hover:text-orange-700">Download Images</div>
              <div className="text-xs text-slate-500 mt-1">ZIP file with images organized by category</div>
            </div>
            <ImageIcon className="text-slate-300 group-hover:text-orange-500" size={24} />
          </button>
        </div>
        
        <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end">
          <button 
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
